import {Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Observable, of} from 'rxjs';
import {finalize, map, switchMap} from 'rxjs/operators';

import {PlanCreationConfirmation, BasicPlanLimit} from '../models/basic-plan-limit.model';
import {
  BasicPlanConfirmationDialogComponent
} from '../basic-plan-limit/basic-plan-confirmation-dialog.component';
import {
  BasicPlanLimitService,
  basicPlanLimitErrorCode,
  basicPlanLimitErrorPayload
} from './basic-plan-limit.service';

/** Runs the shared Basic preflight immediately before any durable plan creation. */
@Injectable({providedIn: 'root'})
export class PlanCreationGuardService {
  constructor(
    private readonly entitlements: BasicPlanLimitService,
    private readonly dialog: MatDialog
  ) {}

  confirmCreate(orgId: string, userId: string, clientId: string | undefined,
                taxYear: number, creationRequestId?: string): Observable<PlanCreationConfirmation | null> {
    // Creation always uses the server result. The UI feature flag controls passive surfaces only.
    return this.entitlements.get(orgId, userId, taxYear, clientId, true, true).pipe(
      switchMap(entitlement => {
        if (!entitlement.basicRestrictionsActive) {
          return of(this.confirmation(entitlement, creationRequestId));
        }

        const dialogRef = this.dialog.open(BasicPlanConfirmationDialogComponent, {
          data: {entitlement},
          maxWidth: '95vw',
          width: '560px'
        });
        return dialogRef.afterClosed().pipe(
          map(confirmed => confirmed && entitlement.canCreate
            ? this.confirmation(entitlement, creationRequestId)
            : null),
          finalize(() => dialogRef.close())
        );
      })
    );
  }

  isCountChanged(error: any): boolean {
    return basicPlanLimitErrorCode(error) === 'BASIC_PLAN_COUNT_CHANGED';
  }

  shouldRetryCountChange(error: any, retryAttempted: boolean): boolean {
    return !retryAttempted && this.isCountChanged(error);
  }

  errorMessage(error: any, fallback: string): string {
    const payload = basicPlanLimitErrorPayload(error);
    return payload?.message || error?.message || fallback;
  }

  private confirmation(entitlement: BasicPlanLimit, creationRequestId?: string): PlanCreationConfirmation {
    const confirmation = this.entitlements.newConfirmation(entitlement);
    if (creationRequestId) {
      confirmation.creationRequestId = creationRequestId;
    }
    return confirmation;
  }
}
